import { t } from "../../../../../utils/i18n";
import React, { useState, useEffect } from "react";
import { Option } from "react-select";
import { Field, FormikErrors, FormikHelpers } from "formik";
import countries from "../../../../../utils/countries.json";
import SelectField from "../common/SelectField";
import { required } from "../../../../../utils/formValidations";
import { IconLocationPurple } from "../../../../Dashboard/dashboardIcons";

interface Props {
  selectedCountry: string;
  apiErrors?: any;
  errors: FormikErrors<{
    card_network: string;
    address: string;
    city: string;
    state: string;
    country: string;
    postal_code: string;
  }>;
  handlePreviousStepChange: (step: string) => void;
  handleNextStepChange: (
    validateForm: () => Partial<FormikHelpers<any>>,
    step: string
  ) => void;
  setFieldValue: (field: string, value: any) => void;
  validateForm: () => Partial<FormikHelpers<any>>;
  updateCountryCode: (countryCode: string) => void;
  isMultiRequest: boolean;
  isVirtualCardRequest: boolean;
}

const MailingDetails: React.FC<Props> = ({
  selectedCountry,
  errors,
  apiErrors,
  handlePreviousStepChange,
  handleNextStepChange,
  setFieldValue,
  validateForm,
  updateCountryCode,
  isMultiRequest,
  isVirtualCardRequest,
}) => {
  useEffect(() => {
    if (selectedCountry) {
      filterStates(selectedCountry);
    }
  }, []);

  useEffect(() => {
    // Initialize all tooltips
    ($('[data-toggle="tooltip"]') as any).tooltip();
  }, []);

  const [stateOptions, setStateOptions] = useState([]);

  const countryOptions = countries
    .filter((country) => country.states.length > 0)
    .map((country) => {
      return {
        value: country.name,
        label: country.name,
        code: country.code2,
      };
    });

  const filterStates = (selectedCountry) => {
    const country = countries.find((ctry) => {
      return ctry.name == selectedCountry;
    });

    if (country) {
      updateCountryCode(country.code2);

      const states = country.states.map((state) => {
        return {
          label: state.name,
          value: state.name,
        };
      });

      setStateOptions(states);
    }
  };

  return (
    <>
      <div className="card-body">
        <div id="mailing-details" className="request-details-cont">
          <div className="request-left-col">
            <i className="fa fa-map-marker card-mail-icon"></i>
            <h4 className="card-reqform-title">
              {isVirtualCardRequest
                ? t("cards.request_form.card_billing_address")
                : t("cards.request_form.card_mailing_address")}
              <i
                data-toggle="tooltip"
                title={t("cards.request_form.please_provide_a_complete_and_accurate")}
                className="fa fa-info-circle"
              ></i>
            </h4>
          </div>
          <div className="row card-deck mb-5 request-right-col">
            <form className="col-md-12">
              <div className="row">
                <div className="col-md-6 form-group">
                  <label htmlFor="address" className="card-sub-title">
                    {t("cards.request_form.address")}
                  </label>
                  <Field
                    name="address"
                    placeholder={t("cards.request_form.house_number_and_street_name")}
                    className={`form-control ${
                      errors.address || apiErrors?.address_error
                        ? "is-invalid"
                        : ""
                    }`}
                    validate={required}
                    error={errors.address || apiErrors?.address_error}
                  />
                  {errors.address && !apiErrors?.address_error && (
                    <small className="error">{errors.address}</small>
                  )}
                  {apiErrors?.address_error && (
                    <small className="error text-danger">
                      {apiErrors.address_error}
                    </small>
                  )}
                </div>
                <div className="col-md-6 form-group">
                  <label htmlFor="country" className="card-sub-title">
                    {t("cards.request_form.country")}
                  </label>
                  <Field
                    name="country"
                    className={`form-control ${
                      errors.country || apiErrors?.country_error
                        ? "is-invalid"
                        : ""
                    }`}
                    component={SelectField}
                    validate={required}
                    options={countryOptions}
                    onChange={(option: Option) => {
                      setFieldValue("country", option.label);

                      filterStates(option.value);
                    }}
                    error={errors.country || apiErrors?.country_error}
                  />
                  {errors.country && !apiErrors?.country_error && (
                    <small className="error">{errors.country}</small>
                  )}
                  {apiErrors?.country_error && (
                    <small className="error text-danger">
                      {apiErrors.country_error}
                    </small>
                  )}
                </div>
              </div>
              <div className="row">
                <div className="col-md-6 form-group">
                  <label htmlFor="city" className="card-sub-title">
                    {t("cards.request_form.city")}
                  </label>
                  <Field
                    name="city"
                    className={`form-control ${
                      errors.city || apiErrors?.city_error ? "is-invalid" : ""
                    }`}
                    validate={required}
                    placeholder={t("cards.request_form.enter_your_city")}
                    error={errors.city || apiErrors?.city_error}
                  />
                  {errors.city && !apiErrors?.city_error && (
                    <small className="error">{errors.city}</small>
                  )}
                  {apiErrors?.city_error && (
                    <small className="error text-danger">
                      {apiErrors.city_error}
                    </small>
                  )}
                </div>
                <div className="col-md-6 form-group">
                  <label htmlFor="state" className="card-sub-title">
                    {t("cards.request_form.state")}
                  </label>
                  <Field
                    name="state"
                    className={`form-control ${
                      errors.state || apiErrors?.state_error ? "is-invalid" : ""
                    }`}
                    validate={required}
                    component={SelectField}
                    options={stateOptions}
                    error={errors.state || apiErrors?.state_error}
                  />
                  {errors.state && !apiErrors?.state_error && (
                    <small className="error">{errors.state}</small>
                  )}
                  {apiErrors?.state_error && (
                    <small className="error text-danger">
                      {apiErrors.state_error}
                    </small>
                  )}
                </div>
              </div>
              <div className="row">
                <div className="col-md-6 form-group">
                  <label htmlFor="postal_code" className="card-sub-title">
                    {t("cards.request_form.postal_code")}
                  </label>
                  <Field
                    className={`form-control ${
                      errors.postal_code || apiErrors?.postal_code_error
                        ? "is-invalid"
                        : ""
                    }`}
                    name="postal_code"
                    validate={required}
                    placeholder={t("cards.request_form.enter_your_postal_code")}
                    error={errors.postal_code || apiErrors?.postal_code_error}
                  />
                  {errors.postal_code && !apiErrors?.postal_code_error && (
                    <small className="error">{errors.postal_code}</small>
                  )}
                  {apiErrors?.postal_code_error && (
                    <small className="error text-danger">
                      {apiErrors.postal_code_error}
                    </small>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
      <div className="p-3">
        <div className="d-flex flex-row justify-content-between">
          <button
            type="button"
            className="btn btn-outline-purple float-left"
            onClick={(e) => handlePreviousStepChange("identification")}
            style={{ visibility: isMultiRequest ? "hidden" : "visible" }}
          >
            <i className="fa fa-arrow-left"></i> {t("cards.request_form.previous")}
          </button>
          <button
            type="button"
            className="btn btn-purple float-right"
            onClick={(e) => handleNextStepChange(validateForm, "preview")}
          >
            {t("cards.request_form.next")} <i className="fa fa-arrow-right"></i>
          </button>
        </div>
      </div>
    </>
  );
};

export default MailingDetails;
